import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { CellClickEvent, GridComponent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { RaciService } from '../../services/raci.service';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { GroupApprovalData } from '../../models/group-approval-data';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Handles portfolio properties component
 */
@Component({
  selector: 'portfolio-properties',
  templateUrl: './portfolio-properties.component.html',
  styleUrls: ['./portfolio-properties.component.css']
})
export class PortfolioPropertiesComponent implements OnInit, OnDestroy {
  /**
 * Input property for grouping by a specific field
 */
  @Input() groupBy: string;

  /**
   * Input property for the active portfolio ID
   */
  @Input() activePortfolioId: any;

  /**
   * Input property for the active task ID
   */
  @Input() activeTaskId: any;

  /**
   * Input property for the active step ID
   */
  @Input() activeStepId: any;

  /**
   * Input property for the active project ID
   */
  @Input() activeProjectId: any;

  /**
   * Input property for the current user
   */
  @Input() currentUser;

  /**
   * Input property for disabling approval
   */
  @Input() disableApproval: boolean;

  /**
   * Setter for the data property
   */
  @Input() set data(value: any) {
    // Code to handle the incoming data

    if (value) {
      this.resetSelections();
      this.properties = value;
      this.filterPropertiesByDataStatus();
      this.approvalMatrixSaved = value[0].approvalUser1 !== null && value[0].approvalUser2 !== null && value[0].approvalUser3 !== null;
      this.properties.forEach(approval => {
        this.mapApprovalDates(approval);
        approval.isSelected = false;
      });
      this.manageProjectApprovals();
    }
  }

  /**
 * Output event emitter for updating the approval matrix
 */
  @Output() onUpdateApprovalMatrix: EventEmitter<any[]> = new EventEmitter<any[]>();

  /**
   * Output event emitter for updating the complete task status
   */
  @Output() updateCompleteTaskStatus: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Output event emitter for showing/hiding a dialog
   */
  @Output() shouldShowDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Output event emitter for refreshing the user task list
   */
  @Output() refreshUserTaskList: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Array to store dirty properties
   */
  dirtyProperties: Array<any> = new Array<any>();

  /**
   * Array to store properties
   */
  properties: any[];

  /**
   * Reference to the current grid component
   */
  currentGridRef: GridComponent;

  /**
   * Selected approval object
   */
  selectedApproval: any;

  /**
   * Flag indicating whether the project approval is selected
   */
  isProjectApprovalSelected: boolean;

  /**
   * Index of the selected approval
   */
  selectedApprovalIndex: number;

  /**
   * Selected group value
   */
  selectedGroup: string;

  /**
   * Array to store properties grouped by sector
   */
  propertiesBySector: Array<GroupApprovalData> = [];

  /**
   * Array to store properties grouped by region
   */
  propertiesByRegion: Array<GroupApprovalData> = [];

  /**
   * Array to store project approval data
   */
  projectApproval: Array<any> = [];

  /**
   * Data for the group
   */
  groupData: any;

  /**
   * Project data
   */
  project: any;

  /**
   * Approval comment
   */
  approvalComment: any;

  /**
   * Valuation reports data
   */
  valuationReports: any;

  /**
   * Selected report ID
   */
  selectedReportId: any;

  /**
   * Flag indicating whether the approval matrix is saved
   */
  approvalMatrixSaved: boolean;

  /**
   * Array of selected approvals
   */
  selectedApprovals: Array<any> = [];

  /**
   * Flag indicating whether the project is selected
   */
  isProjectSelected = false;

  /**
   * Flag indicating whether the approval dialog is displayed
   */
  isApprovalDialogDisplayed = false;

  /**
   * Flag indicating whether the approval is triggered
   */
  isApproveTriggered = false;

  /**
   * Flag indicating whether the task is in progress
   */
  isTaskInProgress = false;

  /**
   * Mode for the selectable component
   */
  mode: SelectableMode = "single";

  /**
   * Flag indicating whether dragging is enabled
   */
  drag = false;

  /**
   * Selectable settings
   */
  selectableSettings: SelectableSettings;

  /**
   * Selected regional group from approval dialog
   */
  selectedRegionalGroup: any;

  /**
   * Selected business unit from approval dialog
   */
  selectedBusinessUnit: any;

  /**
   * Array to store subscriptions
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Constructor
   * @param _raciNavigationService
   * @param appStorage
   * @param eventBus
   * @param _raciService
   */
  constructor(private _raciNavigationService: RaciNavigationService,
    private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private _raciService: RaciService) {
    this.setSelectableSettings();
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    this.project = this.appStorage.getSelectedProject();
    this.subscribeToSaveApprovalEvent();
    this.getReports();

    this._subscriptions.push(this._raciNavigationService._openReport.subscribe(data => {
      if (data) {
        this.createReportLink();
      }
    }));

    this.showRaciApprovalDialog();

    this.eventBus.subscribeEvent("closeRaciApprovalMatrixDialog", () => {
      this.isApprovalDialogDisplayed = false;
    });

    this.eventBus.subscribeEvent("isTaskInProgress", (value) => {
      this.isTaskInProgress = value;
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Filter participants by data status
   */
  filterPropertiesByDataStatus() {
    this.properties = this.properties.filter(property => {
      if (this.properties.filter(item => item.propertyId == property.propertyId).length > 1) {
        return this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "SUCCESS") ??
          this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "ERROR");
      } else {
        return property;
      }
    });
  }

  /**
 * On changes actions
 */
  ngOnChanges() {
    if (this.properties) {
      this.manageProjectApprovals();
      this.groupProperties();
      this.getReports();
    }
  }

  /**
   * Create report link
   */
  createReportLink() {
    if (this.selectedReportId) {
      const absUrl = window.location.href;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/pd
      let url = absUrl.split("#")[0];//http://localhost/polluxApp/polluxValuation/app/pollux.html

      url += "#/rd";
      url += "?ViewReportId=" + this.selectedReportId;//http://localhost/polluxApp/polluxValuation/app/pollux.html#/rd?ViewReportId=1216
      window.open(url, '_blank');
    }
  }

  /**
   * Get Reports of valuation event
   */
  getReports() {
    this._subscriptions.push(this._raciService.getReports(this.activeProjectId, this.activePortfolioId).subscribe(data => {
      if (data) {
        this.valuationReports = data;
      }
    }));
  }

  /**
   * Set Selectable Settings
   */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Create approval based on project
   */
  manageProjectApprovals() {
    this.projectApproval = [{}];
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      this.properties.sort((approvalA, approvalB) => this.getMostCommonApprover(this.properties, approvalIndex, approvalA, approvalB));
      const mostCommonApprover = this.properties[0];
      const pendingApproval = this.properties.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      this.projectApproval[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      this.projectApproval[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      this.projectApproval[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      this.projectApproval[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
   * Groups properties by region
   */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.properties.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.region })) {
        this.propertiesByRegion.push(new GroupApprovalData({ "group": property.region }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.region }).tasks.push(property);
    });

    this.propertiesByRegion.forEach(region => {
      this.setMostCommonApprover(region);
    });

    this.checkForUnknownGroup(this.propertiesByRegion);
  }

  /**
   * Checks for the mos commpon approver
   * @param approvalList Approvals list
   * @param approvalIndex Approval index (eg. 1,2,3)
   * @param approvalA First approval for comparison
   * @param approvalB Second approval for comparison
   */
  private getMostCommonApprover(approvalList: any[], approvalIndex: number, approvalA: any, approvalB: any) {
    return approvalList.filter(v => v[`approval${approvalIndex}`] === approvalB[`approval${approvalIndex}`]).length - approvalList.filter(v => v[`approval${approvalIndex}`] === approvalA[`approval${approvalIndex}`]).length;
  }

  /**
   * Sets most common approvers for specified group
   * @param group Group object
   */
  private setMostCommonApprover(group: any) {
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      const mostCommonApprover = group.tasks.sort((approvalA, approvalB) => this.getMostCommonApprover(group.tasks, approvalIndex, approvalA, approvalB))[0];
      const pendingApproval = group.tasks.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      group.approvals[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      group.approvals[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      group.approvals[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      group.approvals[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
   * Check if the group list contains a gorup without name and sets it name to "Unknown"
   * @param groupList Group list
   */
  private checkForUnknownGroup(groupList: any[]) {
    const unknownSector = groupList.find(item => { return !item.group });
    if (unknownSector) {
      unknownSector.group = "Unknown";
    }
  }

  /**
   * Groups properties by sector
   */
  private groupBySector() {
    this.propertiesBySector = [];
    this.properties.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.sector })) {
        this.propertiesBySector.push(new GroupApprovalData({ "group": property.sector }));
      }
      this.propertiesBySector.find(item => { return item.group == property.sector }).tasks.push(property);
    });

    this.propertiesBySector.forEach(sector => {
      this.setMostCommonApprover(sector);
    });

    this.checkForUnknownGroup(this.propertiesBySector);
  }

  /**
   *  Retieves all grid rows
   */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".properties tr"));
  }

  /**
   * Selection change event for grouped data
   * @param selectedGroup Selected group
   */
  onSelectionChange(selectedGroup: string) {
    this.groupData.forEach(group => {
      if (group.group != selectedGroup) {
        group.selectedRows = [];
      }
    });
  }

  /**
   * Drag start action
   * @param e Event
   * @param grid Grid from which the item is being dragged
   */
  public onDragStart(e: DragEvent, grid: GridComponent): void {
    if (this.disableApproval) return;

    this.currentGridRef = grid;
    const draggedElement: string = (e.target as HTMLElement).innerText;
    if (draggedElement.includes("Name")) {
      e.preventDefault();
    }
  }

  /**
   * On drop to group event
   * @param e
   * @param dataItem
   */
  onDropToGroup(e: DragEvent, dataItem: any) {
    if (this.disableApproval) return;

    const droppedItem = this.getDroppedUser(e);
    this.setGroupApproval(this.getCellIndex(e) - 1, droppedItem, dataItem);
  }

  /**
   * Retrieves dropped user
   * @param e Drag event
   */
  private getDroppedUser(e: DragEvent) {
    const data = e.dataTransfer.getData("text/plain");
    return JSON.parse(data);
  }

  /**
   * Retrieves cell index
   * @param e Drag event
   */
  private getCellIndex(e: DragEvent) {
    const tableCell = document.elementFromPoint(e.pageX, e.pageY) as HTMLTableCellElement;
    let cellIndex = tableCell.cellIndex;

    if (!cellIndex) {
      cellIndex = (tableCell.parentElement as HTMLTableCellElement).cellIndex;
    }

    return cellIndex;
  }

  /**
   * On group selection event
   * @param cell Cell click event
   * @param item Selected item
   */
  onGroupSelection(cell: CellClickEvent, item: any) {
    if (!this.disableApproval && this.dirtyProperties.length == 0) {
      // Click should be enabled only for columns containing approvals
      if (cell.columnIndex > 1) {
        this.selectedApproval = null;
        const dataItem = cell.dataItem;
        this.selectedApprovalIndex = cell.columnIndex - 1;
        this.selectedApproval = dataItem;
        this.approvalComment = '';
        this.selectedGroup = item.group;

        // Prevent selection of approved approvals and other user approvals
        if (!!this.selectedApproval[`dateApproved${this.selectedApprovalIndex}`] || this.selectedApproval[`approval${this.selectedApprovalIndex}`] != this.currentUser.OID) {
          this.selectedApproval = null;
          this.isProjectApprovalSelected = false;
          this.selectedApprovalIndex = null;
        }
        if (this.selectedApproval) {
          this.approveRejectModal();
        }
      }
    }
  }

  /**
  * Opens modal for approval or rejection
  */
  approveRejectModal() {
    if (this.isApproveTriggered) {
      this.approve();
    } else {
      this.reject();
    }
    this.isApprovalDialogDisplayed = false;
  }

  /**
   * On drop action
   * @param e Event
   * @param isProjectApproval Indicating if project approval is being dropped
   */
  public onDrop(e: DragEvent, isProjectApproval: boolean, group: string = null): void {
    if (this.disableApproval) return;

    const droppedItem = this.getDroppedUser(e);
    let tableCell = document.elementFromPoint(e.pageX, e.pageY) as HTMLTableCellElement;
    let cellIndex = tableCell.cellIndex;

    if (!cellIndex) {
      cellIndex = (tableCell.parentElement as HTMLTableCellElement).cellIndex
      tableCell = tableCell.parentElement as HTMLTableCellElement;
    }

    if (isProjectApproval && !this.checkIfUserIsAssignedToProjectApproval(droppedItem)) {
      this.setProjectApproval(cellIndex - 2, droppedItem);
    }
    else {
      const rowIndex = (tableCell.parentElement as HTMLTableRowElement).rowIndex;

      if (!this.checkIfUserIsAssignedToPropertyApproval(rowIndex, droppedItem, group) && !this.checkIfApprovalIsApproved(rowIndex, cellIndex)) {
        this.manageDirtyProperties(rowIndex, cellIndex - 1, droppedItem, group);
      }
      if (cellIndex < 2)
        return;
    }

    this._raciNavigationService._showSaveApprovalIcon.next(true);

    e.preventDefault();
  }

  /**
   * Sets group approval
   */
  setGroupApproval(approvalIndex: number, user: any, group: any) {
    // If user exists in the other approval matrix columns it should not be possible to set him/her as project approver since one approver can not be repeated
    let userExistsInMatrix = false;

    this.groupData.find(x => x.group == group.group).tasks.forEach((property, i) => {
      if (this.checkIfUserIsAssignedToPropertyApproval(i, user, group.group)) {
        const approvals = [property.approvals[0].approval1, property.approvals[0].approval2, property.approvals[0].approval3];
        // skip the current approvalIndex since it doesn't matter if the user exists in the selected column
        approvals.splice(approvalIndex - 1, 1);
        if (approvals.includes(user.oid)) {
          userExistsInMatrix = true;
        }
      }
    });

    if (!userExistsInMatrix && !group.approvals[0][`dateApproved${approvalIndex}`] && !group.approvals[0][`dateRejected${approvalIndex}`]) {
      group.approvals[0][`approvalUser${approvalIndex}`] = user?.name
      group.approvals[0][`approval${approvalIndex}`] = user?.oid;

      group.tasks.forEach(property => {
        const existingDirtyProperty = this.dirtyProperties.find(dp => dp.propertyId == property.propertyId);
        if (existingDirtyProperty) {
          this.populateApprovalInfo(existingDirtyProperty, approvalIndex, user);
        }
        else {
          this.populateApprovalInfo(property, approvalIndex, user);
          this.dirtyProperties.push(property);
          this.shouldShowDialog.emit(true);
        }
      });
    }
  }

  /**
   * Sets project approvals
   * @param projectApprovalIndex Approval index
   * @param user User that is being assigned to approval
   */
  setProjectApproval(projectApprovalIndex: number, user: any) {
    // If user exists in the other approval matrix columns it should not be possible to set him/her as project approver since one approver can not be repeated
    let userExistsInMatrix = false;

    this.properties.forEach((property, i) => {
      if (this.checkIfUserIsAssignedToPropertyApproval(i, user)) {
        const approvals = [property.approval1, property.approval2, property.approval3];
        // skip the current approvalIndex since it doesn't matter if the user exists in the selected column
        approvals.splice(projectApprovalIndex - 1, 1);
        if (approvals.includes(user.oid)) {
          userExistsInMatrix = true;
        }
      }
    });

    if (!userExistsInMatrix && !this.projectApproval[0][`dateApproved${projectApprovalIndex}`] && !this.projectApproval[0][`dateRejected${projectApprovalIndex}`]) {
      this.projectApproval[0][`approvalUser${projectApprovalIndex}`] = user?.name;
      this.projectApproval[0][`approval${projectApprovalIndex}`] = user?.oid;

      this.properties.forEach(property => {
        const existingDirtyProperty = this.dirtyProperties.find(dp => dp.propertyId == property.propertyId);
        if (existingDirtyProperty) {
          this.populateApprovalInfo(existingDirtyProperty, projectApprovalIndex, user);
        }
        else {
          this.populateApprovalInfo(property, projectApprovalIndex, user);
          this.dirtyProperties.push(property);
        }
      });
    }
  }

  /**
   * Sets dorp index
   * @param e Event
   * @param grid Kedno grid
   */
  public onDragOver(e: DragEvent, grid: GridComponent): void {
    e.preventDefault();
  }

  /**
   * On approval selection event
   * @param cell Selected kendo grid cell
   */
  public onApprovalSelection(cell: CellClickEvent, isProjectApproval: boolean) {
    if (!this.disableApproval && this.dirtyProperties.length == 0) {
      this.selectedApproval = null;
      const dataItem = cell.dataItem;
      this.selectedApprovalIndex = this.getApprovalUserIndex(dataItem);
      this.selectedApproval = dataItem;
      this.isProjectApprovalSelected = isProjectApproval;
      this.approvalComment = '';
      this.selectedGroup = null;
    }

    const report = this.valuationReports?.find(report => { return report.PropertyId == cell.dataItem.propertyId })

    if (report) {
      this._raciNavigationService.onSelectRow(true);
      this.selectedReportId = report.Oid;
    }
  }

  /**
   * Give approval to selected approval
   */
  public approve() {
    if (this.isProjectSelected) {
      this.approveOnProjectLevel();
    }
    else if (this.groupBy && this.selectedGroup) {
      this.approveOnGroupLevel();
    }
    else {
      this.singleApprove();
    }
  }

  /**
   * Approve single approval
   */
  private singleApprove() {
    const approvals = [];
    this.selectedApprovals.forEach(approval => {
      approval[`dateApproved${this.getApprovalIndex(approval)}`] = new Date();
      approvals.push(approval);
    });
    this.subscribeToApproveEvent(approvals);
  }

  /**
   * Approve approvals on group level
   */
  private approveOnGroupLevel() {
    const approvals = [];
    const selectedGroups = this.groupData.filter(groupItem => groupItem.isSelected);
    selectedGroups.forEach(group => {
      group.tasks.forEach(property => {
        if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
          this.setPropertyApprovalData(property);
        }
        group.approvals[0][`dateApproved${this.getApprovalIndex(property)}`] = new Date();
        approvals.push(property);
      });
    })
    this.subscribeToApproveEvent(approvals);
  }

  /**
   * Sets property approval data
   * @param property Property data
   */
  private setPropertyApprovalData(property: any) {
    property[`approval${this.getApprovalIndex(property)}`] = this.currentUser.OID;
    property[`approvalUser${this.getApprovalIndex(property)}`] = this.currentUser.User_Description;
    property[`dateApproved${this.getApprovalIndex(property)}`] = new Date();
  }

  /**
   * Give project level approval
   */
  private approveOnProjectLevel() {
    const approvals = [];
    this.selectedApprovals.forEach(property => {
      if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
        this.setPropertyApprovalData(property);
        approvals.push(property);
      }
      this.projectApproval[0][`dateApproved${this.getApprovalIndex(property)}`] = new Date();
    });
    this.subscribeToApproveEvent(approvals);
  }

  /**
 * Emits update task status event
 */
  private emitUpdateTaskStatusEvent() {
    if (!this.properties.find(approval => { return !this.hasAllApprovals(approval) }))
      this.updateCompleteTaskStatus.emit(true);
  }

  /**
 * Checks if approval is approved from all three approvals
 * @param approval Specified approval
 */
  private hasAllApprovals(approval: any): boolean {
    return approval.dateApproved1 && approval.dateApproved2 && approval.dateApproved3;
  }

  /**
   * Subscribes to approve event action on raci service
   * @param approvals Approval data
   */
  private subscribeToApproveEvent(approvals: Array<any>) {
    this._subscriptions.push(this._raciService.approve(approvals, this.approvalComment, this.currentUser.OID).subscribe(data => {
      if (data) {
        this.resetSelections();
        this.refreshProjectAndGroupApprovals();
        this.emitUpdateTaskStatusEvent();
        this.refreshUserTaskList.emit(true);
        approvals.forEach(approval => approval.isSelected = false);
        this.isApprovalDialogDisplayed = false;
        this.groupData.forEach(group => group.isSelected = false);
        this._raciNavigationService.onShowApproveIcon(false);
        this.eventBus.broadcastEvent("closeRaciApprovalMatrixDialog");
      }
    }));
  }

  /**
   * Reject approval task
   */
  public reject() {
    if (this.isProjectSelected) {
      this.rejectOnProjectLevel();
    }
    else if (this.groupBy && this.selectedGroup) {
      this.rejectOnGroupLevel();
    }
    else {
      this.singleRejection();
    }
  }

  /**
   * Reject single approval
   */
  private singleRejection() {
    const approvals = [];
    this.selectedApprovals.forEach(approval => {
      approval[`dateRejected${this.getApprovalIndex(approval)}`] = new Date();
      approvals.push(approval);
    });
    this.subscribeToRejectEvent(approvals);
  }

  /**
   * Sets property rejection data
   * @param property Property info
   */
  private setPropertyRejectionData(property: any) {
    property[`dateRejected${this.getApprovalIndex(property)}`] = new Date();
    property[`approval${this.getApprovalIndex(property)}`] = this.currentUser.OID;
    property[`approvalUser${this.getApprovalIndex(property)}`] = this.currentUser.User_Description;
  }

  /**
   * Reject on group level
   */
  private rejectOnGroupLevel() {
    const approvals = [];
    const selectedGroups = this.groupData.filter(groupItem => groupItem.isSelected);
    selectedGroups.forEach(group => {
      group.tasks.forEach(property => {
        if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
          this.setPropertyRejectionData(property);
          approvals.push(property);
        }
        group.approvals[0][`dateRejected${this.getApprovalIndex(property)}`] = new Date();
      });
    });

    this.subscribeToRejectEvent(approvals);
  }

  /**
   * Reject approvals on project level
   */
  private rejectOnProjectLevel() {
    const approvals = [];
    this.selectedApprovals.forEach(property => {
      if (!property[`dateApproved${this.getApprovalIndex(property)}`]) {
        this.setPropertyRejectionData(property);
        approvals.push(property);
      }
      this.projectApproval[0][`dateRejected${this.getApprovalIndex(property)}`] = new Date();
    });

    this.subscribeToRejectEvent(approvals);
  }

  /**
   * Subscribes to reject event
   * @param approvals Approval data
   */
  private subscribeToRejectEvent(approvals: Array<any>) {
    this._subscriptions.push(this._raciService.rejectApprovals(approvals, this.approvalComment, this.currentUser.OID).subscribe(data => {
      if (data) {
        this.resetSelections();
        this.refreshProjectAndGroupApprovals();
        this.isApprovalDialogDisplayed = false;
        approvals.forEach(approval => approval.isSelected = false);
        this.groupData.forEach(group => group.isSelected = false);
        this.eventBus.broadcastEvent("closeRaciApprovalMatrixDialog");
      }
    }));
  }

  /**
   * Group proerties by sector or region.
   */
  private groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
    this._raciNavigationService.onSelectRow(false);

    this.groupData.forEach(group => {
      const selectedTasks = group.tasks.filter(task => task.isSelected);
      if (selectedTasks.length > 0 && selectedTasks.length == group.tasks.filter(task => !this.isPropertyApproved(task)).length) {
        group.isSelected = true;
      }
    })
  }

  /**
   * Refresh group by/project level approvals
   */
  private refreshProjectAndGroupApprovals() {
    if (!this.groupBy) {
      this.manageProjectApprovals();
    }
    else {
      this.groupProperties();
    }
  }

  /**
   * Resets grid selections
   */
  private resetSelections() {
    this.selectedApproval = null;
    this.selectedApprovalIndex = null;
    this.isProjectApprovalSelected = false;
    this.approvalComment = '';
    this.dirtyProperties = [];
    this.selectedApprovals = [];
    this.isProjectSelected = false;
  }

  /**
   * Checks if user is already in property approvals list
   * @param propertyIndex Collection index of specified property
   * @param user User that is being assigned to approval
   */
  private checkIfUserIsAssignedToPropertyApproval(propertyIndex: number, user: any, group: string = null) {
    const dirtyProperty = group ? this.groupData.find(x => x.group == group).tasks[propertyIndex] : this.properties[propertyIndex];
    return [dirtyProperty.approval1, dirtyProperty.approval2, dirtyProperty.approval3].includes(user.oid);
  }

  /**
  * Checks if user is already in project approvals list
  * @param approvalIndex Collection index of specified approval
  * @param user User that is being assigned to approval
  */
  private checkIfUserIsAssignedToProjectApproval(user: any) {
    this.shouldShowDialog.emit(true);
    return [this.projectApproval[0].approval1, this.projectApproval[0].approval2, this.projectApproval[0].approval3].includes(user.oid);
  }

  /**
 * Checks if approval has approval
 * @param propertyIndex Collection index of specified property
 * @param approvalIndex Approval index (Approval 1, 2, 3)
 */
  private checkIfApprovalIsApproved(propertyIndex: number, approvalIndex: number) {
    this.shouldShowDialog.emit(true);
    const dirtyProperty = this.properties[propertyIndex];
    return dirtyProperty[`dateApproved${approvalIndex}`];
  }

  /**
   * Mark properties as dirty
   * @param propertyIndex Collection index of specified property for which the approval is being created
   * @param approvalIndex Approval index (Approval 1, 2, 3)
   * @param user User that is being assigned to approval
   */
  private manageDirtyProperties(propertyIndex: number, approvalIndex: number, user: any, group: string = null) {
    const dirtyProperty = group ? this.groupData.find(x => x.group == group).tasks[propertyIndex] : this.properties[propertyIndex];
    this.populateApprovalInfo(dirtyProperty, approvalIndex - 1, user);

    const existingDirtyProperty = this.dirtyProperties.find(dp => dp.propertyId == dirtyProperty.propertyId);
    if (!existingDirtyProperty) {
      this.dirtyProperties.push(dirtyProperty);
    }

    this.shouldShowDialog.emit(true);
  }

  /**
   * Populate apporval information
   * @param dirtyProperty Property to which the approval is being assgiend
   * @param approvalIndex  Approval index (Approval 1, 2, 3)
   * @param user User that is being assigned to approval
   */
  private populateApprovalInfo(dirtyProperty: any, approvalIndex: number, user: any) {
    if (!dirtyProperty[`dateApproved${approvalIndex}`] && !dirtyProperty[`dateRejected${approvalIndex}`]) {
      dirtyProperty[`approval${approvalIndex}`] = user?.oid;
      dirtyProperty[`approvalUser${approvalIndex}`] = user?.name;
    }
    dirtyProperty.portfolioId = this.activePortfolioId;
    dirtyProperty.stepId = this.activeStepId;
    dirtyProperty.taskId = this.activeTaskId;
    dirtyProperty.projectId = this.activeProjectId;
  }

  /**
   * Subscribes to save approval event
   */
  private subscribeToSaveApprovalEvent() {
    this._subscriptions.push(this._raciNavigationService._onSaveApproval.subscribe(data => {
      if (data) {
        const unfilledProperties = this.properties.find(property => { return property.approval1 == null || property.approval2 == null || property.approval3 == null })
        if (!unfilledProperties) {
          this.approvalMatrixSaved = true;
          this.onUpdateApprovalMatrix.emit(this.dirtyProperties);
          this.dirtyProperties = [];
        } else {
          this.displayMatrixNotFulfilledModal();
        }
      }
    }));
  }

  /**
   * Displays matrix not fulfilled modal
   */
  private displayMatrixNotFulfilledModal() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      },
    }, 'approvalMatrixNotFulfilled');
  }

  /**
   * Maps approval dates
   * @param approval Approval
   */
  private mapApprovalDates(approval: any) {
    approval.dateApproved1 = approval.dateApproved1 ? new Date(approval.dateApproved1) : null;
    approval.dateApproved2 = approval.dateApproved2 ? new Date(approval.dateApproved2) : null;
    approval.dateApproved3 = approval.dateApproved3 ? new Date(approval.dateApproved3) : null;
    approval.dateRejected1 = approval.dateRejected1 ? new Date(approval.dateRejected1) : null;
    approval.dateRejected2 = approval.dateRejected2 ? new Date(approval.dateRejected2) : null;
    approval.dateRejected3 = approval.dateRejected3 ? new Date(approval.dateRejected3) : null;
  }

  /**
  * On propery select
  * @param selectedItem Selected item
  * @param groupState Is data grouped
  */
  onPropertySelect(selectedItem: any, groupState = false) {
    selectedItem.isSelected = !selectedItem.isSelected;
    if (selectedItem.isSelected) {
      this.selectedApprovals.push(selectedItem);
      if (this.selectedApprovals.length == this.properties.filter(property => !this.isPropertyApproved(property) && (this.currentUser.OID == property.approval1 || this.currentUser.OID == property.approval2 || this.currentUser.OID == property.approval3)).length) {
        this.isProjectSelected = true;
      }
      if (groupState) {
        const group = this.groupData.find(data => {
          return data.tasks.find(task => { return task.propertyId == selectedItem.propertyId; });
        });
        this.shouldGroupBeSeleted(group);
      }
    } else {
      this.removeFromSelectedApprovals(selectedItem);
      if (groupState) {
        this.groupData.find(group => {
          return group.tasks.find(task => { return task.propertyId == selectedItem.propertyId; });
        }).isSelected = false;
      }
    }

    this.shouldShowHideApprovalIcon();
  }

  /**
 * Check should group needs to be selected
 * @param group Group
 */
  shouldGroupBeSeleted(group: any) {
    if (!group.tasks.find(task => !task.isSelected && !this.isPropertyApproved(task) && (this.currentUser.OID == task.approval1 || this.currentUser.OID == task.approval2 || this.currentUser.OID == task.approval3))) {
      group.isSelected = true;
    }
  }

  /**
   * Check is item selecteed
   * @param item Item
   */
  isSelected(item: any): boolean {
    return this.selectedApprovals.find(approval => approval.propertyId == item.propertyId);
  }

  /**
   * Check is property Approved
   * @param item
   */
  isPropertyApproved(item: any): boolean {
    for (let index = 1; index < 4; index++) {
      if (item[`approval${index}`] == this.currentUser.OID) {
        return item[`dateApproved${index}`] != null;
      }
    }
    return false;
  }

  /**
   * Check is property rejected
   * @param item
   */
  isPropertyRejected(item: any): boolean {
    for (let index = 1; index < 4; index++) {
      if (item[`approval${index}`] == this.currentUser.OID) {
        return item[`dateRejected${index}`] != null && !item[`dateApproved${index}`];
      }
    }

    return false;
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = !this.isProjectSelected;
    if (this.isProjectSelected) {
      this.selectedApprovals = [];
      this.properties.forEach(property => {
        this.addToSelectedApprovals(property)
      });
      this.groupData.forEach(group => {
        this.updateGroupSelectionStatus(group);
      });
    } else {
      this.selectedApprovals = [];
      this.groupData.forEach(data => data.isSelected = false);
      this.properties.forEach(property => {
        property.isSelected = false;
      });
    }
    this.shouldShowHideApprovalIcon();
  }

  /**
 * Update group seletion status
 * @param group Group
 */
  updateGroupSelectionStatus(group: any) {
    const unApprovedTask = group.tasks.find(task => {
      return !this.isPropertyApproved(task);
    });
    if (unApprovedTask != null) {
      group.isSelected = true;
    } else {
      group.isSelected = false;
    }
  }

  /**
   * Select all grouped properties
   * @param groupItem Group item
   */
  selectAllPropertiesOfGroup(groupItem: any) {
    this.selectedGroup = groupItem.group;
    groupItem.isSelected = !groupItem.isSelected;
    if (groupItem.isSelected) {
      groupItem.tasks.forEach(task => {
        this.addToSelectedApprovals(task);
      });
      if (this.selectedApprovals.length == this.getUnapprovedProperties().length) {
        this.isProjectSelected = true;
      }
    } else {
      groupItem.tasks.forEach(task => {
        if (!this.isPropertyApproved(task)) {
          task.isSelected = false;
          this.removeFromSelectedApprovals(task);
        }
      });
    }
    this.shouldShowHideApprovalIcon();
  }

  /**
 * Get only unapproved properties
 */
  getUnapprovedProperties() {
    return this.properties.filter(property => !this.isPropertyApproved(property) && (this.currentUser.OID == property.approval1 || this.currentUser.OID == property.approval2 || this.currentUser.OID == property.approval3));
  }

  /**
   * Check is grouped properties approved
   * @param item Grouped item
   */
  isGroupPropertyApproved(groupItem: any): boolean {
    const unApprovedTask = groupItem.tasks.find(task => {
      return !this.isPropertyApproved(task);
    });

    return unApprovedTask == null;
  }

  /**
   * Check is grouped properties rejected
   * @param item Grouped item
   */
  isGroupPropertyRejected(groupItem: any): boolean {
    const unRejectedTask = groupItem.tasks.find(task => {
      return !this.isPropertyRejected(task);
    });

    return unRejectedTask == null;
  }

  /**
   * Check is project selectable
   */
  isProjectSelectable(): boolean {
    const unApprovedTask = this.properties.find(property => {
      return !this.isPropertyApproved(property);
    });

    return !!unApprovedTask;
  }

  /**
 * Check is project rejected
 */
  isProjectRejected(): boolean {
    const approvedTasks = this.properties.filter(property => {
      return this.isPropertyRejected(property);
    });

    return !this.isProjectSelected && !!approvedTasks;
  }

  /**
   * Is project rejected checkbox?
   */
  isProjectRejectedCheckbox(): boolean {
    const approvedTasks = this.properties.filter(property => {
      return this.isPropertyRejected(property);
    });

    return !this.isProjectSelected && approvedTasks?.length == this.properties.length;
  }

  /**
   * Should display sellect all checkbox
   */
  sholudDisplaySellectAll() {
    const assignedProperties = this.properties.filter(property => property.approval1 == this.currentUser.OID || property.approval2 == this.currentUser.OID || property.approval3 == this.currentUser.OID);
    return this.groupBy != '' && this.groupBy != null && assignedProperties.length > 0;
  }

  /**
  * Returns index of approver
  * @param dataItem DataItem
  */
  private getApprovalUserIndex(dataItem: any) {
    if (dataItem.approval1 === this.currentUser.OID) return 1;
    if (dataItem.approval2 === this.currentUser.OID) return 2;
    if (dataItem.approval3 === this.currentUser.OID) return 3;
  }

  /**
   * Check if approved icon appear
   */
  shouldShowHideApprovalIcon() {
    this._raciNavigationService.onShowApproveIcon(this.selectedApprovals.length > 0);
  }

  /**
   * Get approval index
   * @param item Item
   */
  getApprovalIndex(item: any) {
    const keyValues = Object.entries(item);
    for (let i = 1; i < 4; i++) {
      const approval = keyValues.find(entry => entry[0].includes("approval" + i));
      if (approval[1] == this.currentUser.OID) {
        return i;
      }
    }
  }

  /**
   * Add to selected approvals list
   * @param item
   */
  addToSelectedApprovals(item) {
    if (!this.isPropertyApproved(item) && (this.currentUser.OID == item.approval1 || this.currentUser.OID == item.approval2 || this.currentUser.OID == item.approval3)) {
      item.isSelected = true;
      this.selectedApprovals.push(item)
    }
  }

  /**
   * Remove from selected appovals list
   * @param item
   */
  removeFromSelectedApprovals(item) {
    const index = this.selectedApprovals.findIndex(approval => approval.propertyId == item.propertyId);
    this.selectedApprovals.splice(index, 1);
    this.isProjectSelected = false;
  }

  /**
   * Handles show/hide approval dialog
   */
  showRaciApprovalDialog() {
    this._subscriptions.push(this._raciNavigationService._isApprovalDialogDisplayed.subscribe(data => {
      this.isApprovalDialogDisplayed = data;
    }));
  }

  /**
   * Handles on approve action
   */
  onApprove(event: any) {
    if (event) {
      this.approvalComment = event.comment;
      this.isApproveTriggered = event.type === 'approve';
      this.approveRejectModal();
    }
  }
}
